import { ArrowLeft } from 'lucide-react';
import type React from 'react';
import { Link } from 'react-router-dom';
import type { BreadcrumbItem } from '../ui/Breadcrumbs.js';
import { PageHeader, type PageHelpContent } from '../ui/PageHeader.js';
import { Card } from '../tailgrids/core/card.js';
import { buttonStyles } from '../tailgrids/core/button.js';

interface FormPageProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  helpContent?: PageHelpContent;
  /** Where Back and Cancel go (the list, or the entity when editing). */
  backTo: string;
  backLabel?: string;
  /** Extra header actions, e.g. "Ask assistant". Save and Save draft belong in the form's own footer. */
  actions?: React.ReactNode;
  /** The right panel: Field Help (follows the focused field) or the assistant (entity-management §2.3). */
  aside?: React.ReactNode;
  /** The `<Form>` (from `components/form`), whose footer holds the submit button. */
  children: React.ReactNode;
}

/**
 * The create / edit page template (NextAdmin form layout, entity-management §2): back link, title, the form in a card
 * on the left and the Field Help / assistant panel on the right (stacked under the form below `xl`). New entities get
 * a page of their own at `/{entity}/new` — never a modal.
 */
export function FormPage({
  title,
  subtitle,
  breadcrumbs,
  helpContent,
  backTo,
  backLabel = 'Back',
  actions,
  aside,
  children,
}: FormPageProps) {
  return (
    <div className="space-y-5">
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        helpContent={helpContent}
        actions={
          <>
            <Link to={backTo} className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}>
              <ArrowLeft size={14} aria-hidden="true" />
              {backLabel}
            </Link>
            {actions}
          </>
        }
      />
      <div className={aside ? 'grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]' : 'max-w-3xl'}>
        <Card className="min-w-0 p-5 sm:p-6">{children}</Card>
        {aside && <aside className="xl:sticky xl:top-0">{aside}</aside>}
      </div>
    </div>
  );
}
